import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Stack
} from "@mui/material";
import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {FiX, FiPlus} from "react-icons/fi";
import useApiRequest from "../../hooks/useApiRequest";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {snackbar} from "../../utils/snackbar";

const schema = yup.object().shape({
  domain: yup.string().required("El dominio es obligatorio"),
  actions: yup.array().of(yup.string()).min(1, "Agrega al menos una acción"),
  clientCodes: yup.array().of(yup.number())
});

const predefinedActions = ["READ", "WRITE", "DELETE", "ALL"];

const M2MTokenForm = () => {
  const navigate = useNavigate();
  const {create} = useApiRequest({baseUrl: "/m2m-tokens", resource: "m2m_tokens"});

  const {
    register,
    setValue,
    handleSubmit,
    formState: {errors},
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {domain: "", actions: [], clientCodes: []}
  });

  const actions = watch("actions");
  const [resource, setResource] = useState("");
  const [selectedAction, setSelectedAction] = useState(predefinedActions[0]);
  const [clientCodeInput, setClientCodeInput] = useState("");

  const allowedEndings = [":READ", ":WRITE", ":DELETE", ":ALL"];

  const endsWithAllowed = (action) => allowedEndings.some((ending) => action.endsWith(ending) || action === ending);

  const handleAddResourceAction = () => {
    const resourceTrimmed = resource.trim().toUpperCase();
    const actionTrimmed = selectedAction.trim().toUpperCase();
    if (!resourceTrimmed) return;
    const fullAction = `${resourceTrimmed}:${actionTrimmed}`;
    if (!endsWithAllowed(fullAction)) {
      snackbar.error("La acción debe terminar en :READ, :WRITE, :DELETE o :ALL");
      return;
    }
    if (!actions.includes(fullAction)) {
      setValue("actions", [...actions, fullAction]);
    }
    setResource("");
    setSelectedAction(predefinedActions[0]);
  };

  const handleRemoveAction = (actionToRemove) => {
    setValue(
      "actions",
      actions.filter((action) => action !== actionToRemove)
    );
  };

  const onSubmit = async (formData) => {
    const payload = {
      domain: formData.domain,
      actions: formData.actions,
      ...(formData.clientCodes.length > 0 && {clientCodes: formData.clientCodes})
    };

    const result = await create(payload, {silentError: true});
    if (!result.success) {
      if (result.error?.code === "FORBIDDEN") {
        snackbar.error("No tienes permisos suficientes para crear un token M2M.");
      } else {
        snackbar.error(result.userMessage);
      }
      return;
    }

    snackbar.success("Token M2M creado correctamente.");

    if (result.data?.id) {
      navigate("/m2m-tokens-form", {state: {id: result.data.id}});
    } else {
      navigate("/m2m-tokens");
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{display: "flex", flexDirection: "column", gap: 2, minWidth: "300px"}}
    >
      <TextField
        label="Dominio"
        {...register("domain")}
        error={!!errors.domain}
        helperText={errors.domain?.message || "Ejemplo: auth-server"}
        required
        fullWidth
      />

      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Acciones
        </Typography>
        <Stack direction="row" spacing={1} sx={{mb: 2}}>
          <TextField
            label="Recurso"
            value={resource}
            onChange={(e) => setResource(e.target.value)}
            size="small"
            placeholder="Ejemplo: COMPANIES"
          />
          <FormControl size="small">
            <InputLabel>Acción</InputLabel>
            <Select value={selectedAction} label="Acción" onChange={(e) => setSelectedAction(e.target.value)}>
              {predefinedActions.map((action) => (
                <MenuItem key={action} value={action}>
                  {action}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            type="button"
            onClick={handleAddResourceAction}
            disabled={!resource.trim()}
            variant="contained"
            startIcon={<FiPlus />}
          >
            Agregar
          </Button>
        </Stack>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{mb: 1}}>
            Acciones agregadas:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              minHeight: "40px",
              p: 1,
              border: "1px dashed #ccc",
              borderRadius: 1
            }}
          >
            {actions.length > 0 ? (
              actions.map((action) => (
                <Chip
                  key={action}
                  label={action}
                  onDelete={() => handleRemoveAction(action)}
                  deleteIcon={<FiX />}
                  color="primary"
                  variant="filled"
                />
              ))
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{display: "flex", alignItems: "center", width: "100%", justifyContent: "center"}}
              >
                No hay acciones agregadas
              </Typography>
            )}
          </Box>
          {errors.actions && (
            <Typography color="error" variant="caption">
              {errors.actions.message}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{mb: 2}}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Códigos de Cliente (opcional)
        </Typography>
        <Stack direction="row" spacing={1} sx={{mb: 1}}>
          <TextField
            label="Código de cliente"
            value={clientCodeInput}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || /^\d+$/.test(val)) {
                setClientCodeInput(val);
              }
            }}
            size="small"
            placeholder="Ejemplo: 12345"
            inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
            fullWidth
          />
          <Button
            type="button"
            onClick={() => {
              const code = Number(clientCodeInput);
              if (!clientCodeInput || isNaN(code)) {
                return;
              }
              if (!watch("clientCodes").includes(code)) {
                setValue("clientCodes", [...watch("clientCodes"), code]);
                setClientCodeInput("");
              }
            }}
            disabled={!clientCodeInput}
            variant="contained"
            startIcon={<FiPlus />}
          >
            Agregar
          </Button>
        </Stack>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{mb: 1}}>
            Códigos de cliente agregados:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              minHeight: "40px",
              p: 1,
              border: "1px dashed #ccc",
              borderRadius: 1
            }}
          >
            {watch("clientCodes").length > 0 ? (
              watch("clientCodes").map((code) => (
                <Chip
                  key={code}
                  label={code}
                  onDelete={() =>
                    setValue(
                      "clientCodes",
                      watch("clientCodes").filter((c) => c !== code)
                    )
                  }
                  deleteIcon={<FiX />}
                  color="primary"
                  variant="filled"
                />
              ))
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{display: "flex", alignItems: "center", width: "100%", justifyContent: "center"}}
              >
                No hay códigos de cliente agregados
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Button type="submit" variant="contained" color="primary" disabled={actions.length === 0}>
        Crear
      </Button>
    </Box>
  );
};

export default M2MTokenForm;
